'use client';

import Link from 'next/link';

import { Spinner } from '@/components/spinner';
import { cn } from '@/utils/cn';

import type { ButtonProps } from './button.types';
import { cloneIconWithProps, getIconColor, getIconPadding, getIconSize, isIconChild, useButtonLoadingSize } from './button.utils';
import { buttonStyles } from './button.variants';

const BUTTON_TAG_LABELS = {
  new: 'New',
} as const;

export const Button = (props: ButtonProps) => {
  const { variant = 'primary', size = 'medium', className, leadingIcon, trailingIcon, loading, children, disabled, tag, ...rest } = props;

  const { buttonRef, buttonStyle } = useButtonLoadingSize(loading || false);

  const iconChild = isIconChild(children);
  const iconPadding = getIconPadding(iconChild, size);
  const buttonClassNames = cn(buttonStyles({ variant, size }), iconPadding, className);
  const iconSizeForSVG = getIconSize(size);
  const iconColor = getIconColor(variant, disabled);

  const tagBadge = tag ? (
    <span className="absolute -top-2 -right-2 z-10 px-1.5 py-0.5 text-[10px] leading-none font-medium text-highlight bg-surface-secondary border border-highlight rounded-full pointer-events-none">
      {BUTTON_TAG_LABELS[tag]}
    </span>
  ) : null;

  const content = (
    <>
      {tagBadge}
      {(leadingIcon || loading) && (
        <span>
          {loading ? <Spinner size={iconSizeForSVG} className={iconColor} /> : cloneIconWithProps(leadingIcon, iconSizeForSVG, iconColor)}
        </span>
      )}
      {loading ? <span className="sr-only">{children}</span> : children}
      {trailingIcon && !loading && cloneIconWithProps(trailingIcon, iconSizeForSVG, iconColor)}
    </>
  );

  if ('href' in rest && rest.href) {
    const { ref, href, ...anchorProps } = rest;

    if (href.startsWith('/')) {
      return (
        <Link
          href={href}
          style={buttonStyle}
          className={cn(buttonClassNames, loading && 'pointer-events-none', disabled && 'pointer-events-none opacity-50')}
          {...anchorProps}
          aria-busy={loading || props['aria-busy']}
        >
          {content}
        </Link>
      );
    }

    return (
      <a
        ref={ref}
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        style={buttonStyle}
        className={cn(buttonClassNames, loading && 'pointer-events-none', disabled && 'pointer-events-none opacity-50')}
        {...anchorProps}
        aria-busy={loading || props['aria-busy']}
      >
        {content}
      </a>
    );
  }

  const { ref, ...buttonProps } = rest as Exclude<typeof rest, { href: string }>;

  return (
    <button
      ref={ref || buttonRef}
      style={buttonStyle}
      disabled={disabled}
      className={cn(buttonClassNames, loading && 'pointer-events-none')}
      {...buttonProps}
      aria-busy={loading || props['aria-busy']}
    >
      {content}
    </button>
  );
};
